// Search Form Component - Advanced filtering for EOL/EOS products

import React, { useState, useEffect } from 'react';
import {
  Stack,
  TextField,
  PrimaryButton,
  DefaultButton,
  Dropdown,
  Label,
  useTheme,
  mergeStyleSets,
} from '@fluentui/react';
import { EditableDatePicker } from '../../../components/EditableDatePicker';
import type { IDropdownOption } from '@fluentui/react';
import type { SearchCriteria } from '../types';
import { useEolEos } from '../contexts';
import { formatDateForApi } from '../utils/formatters';

interface SearchFormProps {
  onSearch: (criteria: SearchCriteria) => void;
  onClear: () => void;
}

export const SearchForm: React.FC<SearchFormProps> = ({ onSearch, onClear }) => {
  const theme = useTheme();
  const { getManufacturers } = useEolEos();

  const [manufacturer, setManufacturer] = useState<string>('');
  const [productName, setProductName] = useState<string>('');
  const [productNumber, setProductNumber] = useState<string>('');
  const [eolDateFrom, setEolDateFrom] = useState<Date | null>(null);
  const [eolDateTo, setEolDateTo] = useState<Date | null>(null);
  const [eosDateFrom, setEosDateFrom] = useState<Date | null>(null);
  const [eosDateTo, setEosDateTo] = useState<Date | null>(null);
  const [manufacturerOptions, setManufacturerOptions] = useState<IDropdownOption[]>([]);

  const styles = mergeStyleSets({
    formContainer: {
      padding: '24px',
      backgroundColor: theme.palette.white,
      border: `1px solid ${theme.palette.neutralLight}`,
      borderRadius: '4px',
    },
    section: {
      marginBottom: '16px',
    },
    dateRangeContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '16px',
    },
  });

  // Load manufacturers on mount
  useEffect(() => {
    const loadManufacturers = async () => {
      const manufacturers = await getManufacturers();
      if (manufacturers) {
        const options: IDropdownOption[] = [
          { key: '', text: '(All Manufacturers)' },
          ...manufacturers.map((name) => ({
            key: name,
            text: name,
          })),
        ];
        setManufacturerOptions(options);
      }
    };
    void loadManufacturers();
  }, [getManufacturers]);

  const handleSearch = () => {
    const criteria: SearchCriteria = {};

    if (manufacturer) criteria.manufacturer = manufacturer;
    if (productName) criteria.productName = productName;
    if (productNumber) criteria.productNumber = productNumber;
    if (eolDateFrom) criteria.endOfSaleDateFrom = formatDateForApi(eolDateFrom) || undefined;
    if (eolDateTo) criteria.endOfSaleDateTo = formatDateForApi(eolDateTo) || undefined;
    if (eosDateFrom) criteria.endOfSupportDateFrom = formatDateForApi(eosDateFrom) || undefined;
    if (eosDateTo) criteria.endOfSupportDateTo = formatDateForApi(eosDateTo) || undefined;

    onSearch(criteria);
  };

  const handleClear = () => {
    setManufacturer('');
    setProductName('');
    setProductNumber('');
    setEolDateFrom(null);
    setEolDateTo(null);
    setEosDateFrom(null);
    setEosDateTo(null);
    onClear();
  };

  return (
    <Stack className={styles.formContainer} tokens={{ childrenGap: 16 }}>
      <Stack.Item className={styles.section}>
        <Dropdown
          label="Manufacturer"
          placeholder="Select a manufacturer"
          options={manufacturerOptions}
          selectedKey={manufacturer}
          onChange={(_e, option) => setManufacturer(option?.key as string || '')}
        />
      </Stack.Item>

      <Stack horizontal tokens={{ childrenGap: 16 }}>
        <Stack.Item grow>
          <TextField
            label="Product Name"
            placeholder="Search by product name"
            value={productName}
            onChange={(_e, newValue) => setProductName(newValue || '')}
          />
        </Stack.Item>
        <Stack.Item grow>
          <TextField
            label="Product Number"
            placeholder="Search by product number"
            value={productNumber}
            onChange={(_e, newValue) => setProductNumber(newValue || '')}
          />
        </Stack.Item>
      </Stack>

      <Stack.Item className={styles.section}>
        <Label>End of Sale Date Range</Label>
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <Stack.Item grow>
            <EditableDatePicker
              placeholder="From date"
              value={eolDateFrom || undefined}
              onSelectDate={(date) => setEolDateFrom(date || null)}
            />
          </Stack.Item>
          <Stack.Item grow>
            <EditableDatePicker
              placeholder="To date"
              value={eolDateTo || undefined}
              onSelectDate={(date) => setEolDateTo(date || null)}
            />
          </Stack.Item>
        </Stack>
      </Stack.Item>

      <Stack.Item className={styles.section}>
        <Label>End of Support Date Range</Label>
        <Stack horizontal tokens={{ childrenGap: 16 }}>
          <Stack.Item grow>
            <EditableDatePicker
              placeholder="From date"
              value={eosDateFrom || undefined}
              onSelectDate={(date) => setEosDateFrom(date || null)}
            />
          </Stack.Item>
          <Stack.Item grow>
            <EditableDatePicker
              placeholder="To date"
              value={eosDateTo || undefined}
              onSelectDate={(date) => setEosDateTo(date || null)}
            />
          </Stack.Item>
        </Stack>
      </Stack.Item>

      <Stack horizontal tokens={{ childrenGap: 8 }}>
        <PrimaryButton text="Search" onClick={handleSearch} iconProps={{ iconName: 'Search' }} />
        <DefaultButton text="Clear Filters" onClick={handleClear} iconProps={{ iconName: 'ClearFilter' }} />
      </Stack>
    </Stack>
  );
};
